import { Layout } from "./Layout";
import { Breadcrumbs } from "./Breadcrumbs";
import { motion } from "framer-motion";
import { BlogCard, type BlogPost } from "./BlogCard";
import { CheckCircle2, ArrowRight, HelpCircle, Users, Link2, Smartphone, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ALL_POSTS } from "@/data/blogPosts";
import { BlogService } from "@/lib/blog-service";
import { memo, useMemo } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

interface StepItem {
  title: string;
  description: string;
}

interface FeatureItem {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

export interface SEOPageProps {
  title: string;
  subtitle: string;
  heroImage?: string;
  variant?: "default" | "centered" | "split";
  introParagraphs: string[];
  benefits: FeatureItem[];
  howItWorks: StepItem[];
  diferenciais: FeatureItem[];
  faq: FAQItem[];
  breadcrumbs: { label: string; href: string }[];
  category: string;
  relatedArticles?: { title: string; href: string }[];
  blogPosts?: BlogPost[];
  customHeader?: React.ReactNode;
  ctaTitle?: string;
  ctaDescription?: string;
  ctaButtonText?: string;
  showPriceTable?: boolean;
  showTestimonials?: boolean;
  hideStats?: boolean;
  hideHowItWorks?: boolean;
  hideBenefits?: boolean;
  hideDiferenciais?: boolean;
  sections?: {
    title: string;
    content: React.ReactNode;
    type?: "default" | "grid" | "featured";
  }[];
}

/**
 * Performance-Optimized Schema Generator
 */
const PageSchema = memo(({ 
  title, 
  subtitle, 
  heroImage, 
  category, 
  faq, 
  breadcrumbs, 
  currentHref 
}: any) => {
  const schemas = useMemo(() => {
    const baseUrl = "https://p2propaineliptv.com.br";
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faq.map((item: any) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };

    // Choose schema type by route shape: blog post → Article, /blog index → CollectionPage, else WebPage
    const href: string = currentHref ?? "";
    const isBlogPost = href.startsWith("/blog/") && href.length > "/blog/".length;
    const isBlogIndex = href === "/blog" || href === "/blog/";
    const schemaType = isBlogPost ? "Article" : isBlogIndex ? "CollectionPage" : "WebPage";

    const articleSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": schemaType,
      headline: title,
      name: title,
      description: subtitle,
      image: heroImage ? [heroImage] : [`${baseUrl}/p2pro-logo.webp`],
      publisher: {
        "@type": "Organization",
        name: "P2 PRO",
        logo: {
          "@type": "ImageObject",
          "url": "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
          "width": 112,
          "height": 112
        },
      },
      articleSection: category,
      inLanguage: "pt-BR",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${baseUrl}${currentHref}`
      }
    };

    if (isBlogPost) {
      articleSchema.author = { "@type": "Organization", name: "P2 PRO", url: baseUrl };
      articleSchema.datePublished = "2026-01-01";
      articleSchema.dateModified = "2026-06-17";
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item: any, index: number) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.label,
        "item": `${baseUrl}${item.href}`
      }))
    };

    return [faqSchema, articleSchema, breadcrumbSchema];
  }, [title, subtitle, heroImage, category, faq, breadcrumbs, currentHref]);

  return (
    <>
      {schemas.map((schema, i) => (
        <script 
          key={i}
          type="application/ld+json" 
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} 
        />
      ))}
    </>
  );
});

PageSchema.displayName = "PageSchema";

export const SEOPage = memo(({
  title,
  subtitle,
  heroImage,
  variant = "default",
  hideStats = false,
  hideHowItWorks = false,
  hideBenefits = false,
  hideDiferenciais = false,
  introParagraphs,
  benefits,
  howItWorks,
  diferenciais,
  faq,
  breadcrumbs,
  category,
  relatedArticles = [],
  ctaTitle,
  ctaDescription,
  ctaButtonText,
  showPriceTable = false,
  showTestimonials = false,
  sections = [],
  blogPosts = [],
  customHeader
}: SEOPageProps) => {
  const whatsappUrl = `https://wa.me/5583986147064?text=${encodeURIComponent("Olá! Vim do site p2propaineliptv.com.br, gostaria de mais informações.")}`;
  const currentHref = breadcrumbs?.[breadcrumbs.length - 1]?.href;

  const autoRelated = useMemo(() => {
    if (relatedArticles.length > 0) return relatedArticles;
    return BlogService.getRelatedArticles(ALL_POSTS, category, currentHref);
  }, [relatedArticles, category, currentHref]);

  const appLinks = useMemo(() => [
    { name: "One X", href: "/aplicativos-iptv/one-x", desc: "Player oficial multi-dispositivo" },
    { name: "P2 PRO", href: "/aplicativos-iptv", desc: "App flagship com máxima performance" },
    { name: "P2 PLAY", href: "/aplicativos-iptv", desc: "Interface intuitiva e leve" },
    { name: "Prime Play", href: "/aplicativos-iptv", desc: "Experiência premium completa" },
  ], []);

  return (
    <Layout>
      <PageSchema 
        title={title}
        subtitle={subtitle}
        heroImage={heroImage}
        category={category}
        faq={faq}
        breadcrumbs={breadcrumbs}
        currentHref={currentHref}
      />

      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <Breadcrumbs items={breadcrumbs} />

          {/* Hero Section */}
          <section className={cn(
            "grid gap-12 items-center mb-24",
            variant === "centered" ? "text-center max-w-4xl mx-auto" : "lg:grid-cols-2"
          )}>
            <motion.div
              initial={{ opacity: 1, x: 0, y: 0 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className={cn(
                "inline-block px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-6",
                variant === "split" ? "bg-white/10 text-white" : "bg-electric/10 border border-electric/20 text-electric"
              )}>
                {category}
              </span>
              <h1 className={cn(
                "text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.1] mb-8",
                variant === "split" ? "italic uppercase" : ""
              )}>
                {title}
              </h1>
              <p className={cn(
                "text-lg md:text-xl text-muted-foreground leading-relaxed mb-10",
                variant === "centered" ? "mx-auto" : "max-w-xl"
              )}>
                {subtitle}
              </p>
              <div className={cn("flex flex-wrap gap-4", variant === "centered" && "justify-center")}>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "px-8 py-4 rounded-full font-black text-xs uppercase tracking-[0.2em] hover:shadow-glow hover:scale-105 transition-all flex items-center gap-3",
                    variant === "split" ? "bg-white text-black" : "bg-electric text-white shadow-xl"
                  )}
                >
                  Falar no WhatsApp
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>

            {variant !== "centered" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className={cn(
                  "relative aspect-video rounded-[2.5rem] overflow-hidden border border-white/5 shadow-2xl",
                  variant === "split" ? "bg-electric" : "bg-white/[0.02]"
                )}
              >
                {heroImage ? (
                  <img src={heroImage} alt={title} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-electric/5 to-transparent">
                    <div className={cn(
                      "w-20 h-20 rounded-3xl flex items-center justify-center mb-6",
                      variant === "split" ? "bg-white/20 text-white" : "bg-electric/10 text-electric"
                    )}>
                      <Users className="w-10 h-10" />
                    </div>
                    <div className="text-center px-8">
                      <h3 className="text-xl font-black uppercase tracking-tighter mb-2 italic">Infraestrutura P2 PRO</h3>
                      <p className="text-muted-foreground text-sm max-w-[240px] mx-auto italic">Tecnologia de ponta para sua revenda IPTV em 2026.</p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </section>

          {/* Custom Header (Filters, etc) */}
          {customHeader && (
            <div className="mb-12">
              {customHeader}
            </div>
          )}

          {/* Blog Posts Grid */}
          {blogPosts.length > 0 && (
            <section className="mb-24">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogPosts.map((post, i) => (
                  <BlogCard key={post.title} post={post} index={i} />
                ))}
              </div>
            </section>
          )}

          {/* Introduction */}
          <section className="mb-24 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">Introdução</h2>
            <div className="space-y-6">
              {introParagraphs.map((p, i) => {
                // Link injection for authority building (only on the first paragraph for balance)
                if (i === 0) {
                  const keywords = [
                    "Painel IPTV Pós Pago",
                    "revenda IPTV",
                    "servidor IPTV",
                    "Painel IPTV",
                    "Revendedor IPTV"
                  ];
                  
                  let modifiedParagraph = p;
                  for (const keyword of keywords) {
                    if (p.toLowerCase().includes(keyword.toLowerCase())) {
                      const regex = new RegExp(`(${keyword})`, 'i');
                      modifiedParagraph = p.replace(regex, `<a href="/" class="text-electric font-bold hover:underline">$1</a>`);
                      break; // Only replace one keyword per paragraph for natural look
                    }
                  }

                  return (
                    <p 
                      key={i} 
                      className="text-lg text-muted-foreground leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: modifiedParagraph }}
                    />
                  );
                }
                
                return (
                  <p key={i} className="text-lg text-muted-foreground leading-relaxed">
                    {p}
                  </p>
                );
              })}
            </div>
          </section>

          {/* Internal Links Block — Main Page + App Pages */}
          <section className="mb-24">
            <div className="p-8 md:p-12 rounded-[2.5rem] bg-gradient-to-br from-electric/10 to-transparent border border-electric/20">
              <div className="flex items-center gap-3 mb-6">
                <Link2 className="w-5 h-5 text-electric" />
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-electric">Links recomendados</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-black tracking-tighter mb-8">
                Aprofunde-se em <span className="text-electric italic">conteúdos essenciais</span>
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <a
                  href="/"
                  className="group p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-electric/40 transition-all"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-black uppercase tracking-widest text-electric">Página Principal</span>
                  </div>
                  <h4 className="text-lg font-bold mb-2 group-hover:text-electric transition-colors">
                    Painel IPTV Pós Pago — Guia Definitivo
                  </h4>
                  <p className="text-sm text-muted-foreground">Conheça a infraestrutura completa P2 PRO e como começar sem investimento inicial.</p>
                  <div className="mt-4 text-[10px] font-black uppercase tracking-widest text-electric flex items-center gap-2">
                    Acessar página principal <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </a>
                <a
                  href="/aplicativos-iptv"
                  className="group p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-electric/40 transition-all"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone className="w-3 h-3 text-electric" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-electric">Aplicativos Compatíveis</span>
                  </div>
                  <h4 className="text-lg font-bold mb-2 group-hover:text-electric transition-colors">
                    Aplicativos IPTV oficiais
                  </h4>
                  <p className="text-sm text-muted-foreground">One X, P2 PRO, P2 PLAY e Prime Play — players otimizados para revendedores.</p>
                  <div className="mt-4 text-[10px] font-black uppercase tracking-widest text-electric flex items-center gap-2">
                    Ver aplicativos <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </a>
              </div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                {appLinks.map((app) => (
                  <a
                    key={app.name}
                    href={app.href}
                    className="p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-electric/30 hover:bg-electric/5 transition-all text-center group"
                  >
                    <div className="font-black text-sm group-hover:text-electric transition-colors">{app.name}</div>
                    <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{app.desc}</div>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* Mid-article CTA */}
          <section className="mb-24">
            <div className="relative overflow-hidden p-10 md:p-14 rounded-[2.5rem] bg-electric/10 border border-electric/30 text-center">
              <div className="absolute top-0 right-0 w-72 h-72 bg-electric/20 blur-[100px] rounded-full -mr-36 -mt-36" />
              <div className="relative z-10 max-w-2xl mx-auto">
                <MessageCircle className="w-10 h-10 text-electric mx-auto mb-4" />
                <h3 className="text-2xl md:text-4xl font-black tracking-tighter italic uppercase mb-4">
                  Pronto para começar <span className="text-electric">sem investir nada?</span>
                </h3>
                <p className="text-muted-foreground mb-8">
                  Fale agora com um consultor P2 PRO e libere seu painel pós-pago em minutos.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 rounded-full font-black text-xs uppercase tracking-[0.2em] shadow-[0_10px_30px_rgba(37,211,102,0.3)] hover:scale-105 transition-transform"
                >
                  Quero minha estrutura <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </section>

          {/* Custom Dynamic Sections */}
          {sections.map((section, idx) => (
            <section key={idx} className="mb-24">
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-12">{section.title}</h2>
              <hr className="border-white/10 mb-12" />
              <div className={cn(
                section.type === "grid" ? "grid md:grid-cols-2 lg:grid-cols-3 gap-8" : "",
                section.type === "featured" ? "p-12 rounded-[3rem] bg-electric/10 border border-electric/20 backdrop-blur-sm" : ""
              )}>
                {section.content}
              </div>
            </section>
          ))}

          {/* Benefits */}
          {!hideBenefits && (
            <section className="mb-24">
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-12 text-center">Benefícios Exclusivos</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="p-8 rounded-3xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-electric/10 flex items-center justify-center mb-6 text-electric group-hover:scale-110 transition-transform">
                      {benefit.icon || <CheckCircle2 className="w-6 h-6" />}
                    </div>
                    <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{benefit.description}</p>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* How It Works */}
          {!hideHowItWorks && (
            <section className="mb-24 py-24 px-8 rounded-[3rem] bg-electric/5 border border-electric/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-electric/10 blur-[120px] rounded-full -mr-48 -mt-48" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-16 text-center">Como Funciona</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
                  {howItWorks.map((step, i) => (
                    <div key={i} className="relative pt-12">
                      <div className="text-6xl font-black text-electric/10 absolute top-0 left-0 leading-none select-none">
                        0{i + 1}
                      </div>
                      <div className="relative z-10">
                        <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Performance Stats/Trust Signals */}
          {showPriceTable && !hideStats && (
            <section className="mb-24">
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-12 text-center italic">
                Nossa <span className="text-electric">Performance</span> Global
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { label: "Uptime do Servidor", value: "99.9%", desc: "Garantia de estabilidade absoluta 24h por dia.", icon: "⚡" },
                  { label: "Clientes Ativos", value: "+50k", desc: "Milhares de usuários conectados simultaneamente.", icon: "👥", highlight: true },
                  { label: "Canais & VOD", value: "+100k", desc: "O maior catálogo de conteúdos do mercado.", icon: "🎬" }
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -10 }}
                    className={cn(
                      "p-10 rounded-[2.5rem] border transition-all text-center relative overflow-hidden",
                      stat.highlight 
                        ? "bg-electric border-electric text-white shadow-[0_20px_50px_rgba(0,210,255,0.3)]" 
                        : "bg-white/[0.02] border-white/5"
                    )}
                  >
                    <div className="text-4xl mb-6">{stat.icon}</div>
                    <div className="text-4xl font-black italic mb-2 tracking-tighter">{stat.value}</div>
                    <h3 className={cn("text-lg font-bold mb-4 uppercase tracking-widest", stat.highlight ? "text-white" : "text-electric")}>
                      {stat.label}
                    </h3>
                    <p className={cn("text-sm leading-relaxed", stat.highlight ? "text-white/80" : "text-muted-foreground")}>
                      {stat.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* Diferenciais */}
          {!hideDiferenciais && (
            <section className="mb-24">
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-12">Diferenciais P2 PRO</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {diferenciais.map((item, i) => (
                  <div key={i} className="flex gap-6 p-6 rounded-2xl hover:bg-white/[0.01] transition-colors">
                    <div className="mt-1">
                      <CheckCircle2 className="w-6 h-6 text-electric" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Testimonials Section */}
          {showTestimonials && (
            <section className="mb-24">
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-12 text-center italic">
                Quem <span className="text-electric">Confia</span> Lucra
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { name: "Ricardo Silva", role: "Revendedor Master", text: "O sistema pós-pago mudou meu jogo. Não preciso mais empatar 2 mil reais em créditos todo mês. Pago conforme vendo e lucro muito mais.", avatar: "https://i.pravatar.cc/150?u=ricardo" },
                  { name: "Ana Oliveira", role: "Franqueada P2 PRO", text: "Minha marca própria cresceu 300% em 4 meses. A estabilidade do servidor P2PRO é o que mantém meus clientes renovando todos os meses.", avatar: "https://i.pravatar.cc/150?u=ana" },
                  { name: "Marcos Souza", role: "Revendedor Iniciante", text: "Comecei com investimento zero e em 15 dias já tinha 20 clientes ativos. O painel é muito fácil de usar pelo celular.", avatar: "https://i.pravatar.cc/150?u=marcos" }
                ].map((testimonial, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.02 }}
                    className="p-8 rounded-[2rem] bg-white/[0.02] border border-white/5 relative"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full border-2 border-electric/30" />
                      <div>
                        <div className="font-bold text-sm text-white">{testimonial.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-electric font-black">{testimonial.role}</div>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm italic leading-relaxed">"{testimonial.text}"</p>
                    <div className="mt-6 flex gap-1 text-yellow-500 text-xs">
                      {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {/* FAQ Section */}
          <section className="mb-24">
            <div className="flex flex-col items-center mb-16">
              <HelpCircle className="w-12 h-12 text-electric mb-6" />
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-center">Dúvidas Frequentes</h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {faq.map((item, i) => (
                <div key={i} className="p-8 rounded-2xl bg-white/[0.02] border border-white/5">
                  <h3 className="text-lg font-bold mb-4">{item.question}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="mb-24 py-16 px-8 rounded-[3rem] bg-white/[0.02] border border-white/5 relative overflow-hidden text-center">
            <div className="max-w-3xl mx-auto relative z-10">
              {!ctaTitle && (
                <div className="flex justify-center -space-x-4 mb-8">
                  {[
                    "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=120&h=120",
                    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=120&h=120",
                    "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=120&h=120"
                  ].map((src, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      viewport={{ once: true }}
                      className="w-16 h-16 rounded-full border-4 border-[#0a0c10] overflow-hidden shadow-xl"
                    >
                      <img src={src} alt="Consultor de revenda IPTV P2 PRO" className="w-full h-full object-cover" />
                    </motion.div>
                  ))}
                </div>
              )}

              <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-8 italic uppercase">
                {ctaTitle || (
                  <>Ainda com <span className="text-electric">dúvidas?</span></>
                )}
              </h2>
              <p className="text-muted-foreground text-lg mb-10 max-w-xl mx-auto italic">
                {ctaDescription || "Nossos consultores especializados estão online agora para ajudar você a escolher a melhor estratégia para sua revenda."}
              </p>

              <div className="flex flex-col items-center gap-6">
                <motion.a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative group inline-flex items-center gap-4 bg-[#25D366] text-white px-10 py-5 rounded-full font-black text-xs uppercase tracking-[0.2em] shadow-[0_15px_40px_rgba(37,211,102,0.3)] transition-all overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
                  <span className="relative z-10">{ctaButtonText || "Falar com nossos consultores"}</span>
                  <div className="relative w-3 h-3 bg-white rounded-full">
                    <div className="absolute inset-0 bg-white rounded-full animate-ping" />
                  </div>
                </motion.a>
                
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-[#25D366] animate-pulse">
                  <div className="w-2 h-2 bg-[#25D366] rounded-full" />
                  Atendimento Online Agora
                </div>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {autoRelated.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold mb-8">Conteúdos Relacionados</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {autoRelated.map((article, i) => (
                  <a
                    key={i}
                    href={article.href}
                    className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group"
                  >
                    <h3 className="font-bold group-hover:text-electric transition-colors">{article.title}</h3>
                    <div className="mt-4 text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                      Ler artigo <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </Layout>
  );
});

SEOPage.displayName = "SEOPage";
